<template>
  <div>
    <DSelect :items="itemsWithOccupation"
             :event="event"
             :isDeleteAllAnOption="isDeleteAllAnOption"
             :itemsUnselectable="itemsUnselectable"
             :isLoadingAvailability="isLoading"
             :isLoading="isLoadingProp"
             :itemsSelected="selectedItems"
             :itemsThatCantBeDeleted="itemsThatCantBeDeleted"
             :multiple="isMultiple"
             :placeholder="i18n[locale]['search']"
             :appendToBody="appendToBody"
             :calculate-position="withPopper"
             :show="show"
             :showCreateButton="showCreateButton"
             :newUserCalendarId="newUserCalendarId"
             :disableAddMore="disableAddMore"
             :isShowCollisions="true"
             @update="setSelectedItems"
             @createNew="createNew"
             @showCalendar="showCalendar">
      <h4 slot="noMatch" v-if="disableAddMore">{{ disableAddMoreMessage }}</h4>
    </DSelect>
    <CalendarViewDay
      v-if="activeCalendar"
      :event="event"
      :date="date"
      :calendar="activeCalendar"
      :calendars="items"
      @updateAndClose="updateAndClose"
      @close="activeCalendar = null">
    </CalendarViewDay>
  </div>
</template>

<script>
import CalendarViewDay from '../Calendars/CalendarViewDay'
import DSelect from './DSelectCals'
import {createPopper} from '@popperjs/core'
import {mapGetters} from 'vuex'
import docendo from '../../docendo'
import {maskHiddenAbsenceTitlesInAvailability} from '../../utils/hiddenAbsenceTitle'

export default {
  components: {DSelect, CalendarViewDay},
  props: {
    itemType: {
      default: 'user'
    },
    preSelectedItems: {
      default: () => []
    },
    itemsThatCantBeDeleted: {
      default: () => []
    },
    itemsUnselectable: {
      default: () => []
    },
    includeEventCalendar: {
      // Keep the event owner calendar in the list,
      // e.g. so the owner can be chosen as substitute on their own activity
      type: Boolean,
      default: false
    },
    showTags: {
      type: Boolean,
      default: false
    },
    isMultiple: {
      type: Boolean,
      default: true
    },
    showCreateButton: {
      type: Boolean,
      default: false
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    show: {
      default: false,
      type: Boolean
    },
    eventId: {
      default: null
    },
    event: {
      default: null
    },
    disableAddMore: {
      type: Boolean,
      default: false
    },
    disableAddMoreMessage: {
      type: String
    },
    isDeleteAllAnOption: {
      type: Boolean,
      default: true
    },
    isLoadingProp: {
      type: Boolean,
      default: false
    },
    eventSchoolCalendars: {
      required: false,
      type: Array,
      default: () => []
    },
    eventSchoolUsers: {
      required: false,
      type: Array,
      default: () => []
    },
    eventSchoolYears: {
      required: false,
      type: Array,
      default: () => []
    },
    isEventSchoolIdTheSameAsActiveSchoolId: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      activeCalendar: null,
      isLoaded: false,
      tags: [],
      selectedItems: [],
      occupiedItems: {},
      presentItems: {},
      searchText: '',
      placement: 'bottom',
      i18n: {
        da: {
          search: 'Søg og vælg'
        },
        en: {
          search: 'Search and choose'
        },
        nb: {
          search: 'Søk og velg'
        }
      },
      newUserCalendarId: null,
      isLoading: false,
      theShowCalendar: null
    }
  },
  computed: {
    ...mapGetters([
      'locale'
    ]),
    calendars () {
      return this.isEventSchoolIdTheSameAsActiveSchoolId ? this.$store.state.calendars : this.eventSchoolCalendars
    },
    users () {
      return this.isEventSchoolIdTheSameAsActiveSchoolId ? this.$store.state.allUsers : this.eventSchoolUsers
    },
    activeSchoolYears () {
      return this.isEventSchoolIdTheSameAsActiveSchoolId ? this.$store.state.activeSchoolYears : this.eventSchoolYears
    },
    eventSchoolYearId () {
      if (!this.event || !this.event.start || !this.activeSchoolYears || !Array.isArray(this.activeSchoolYears) || this.activeSchoolYears.length === 0) {
        return null
      }
      const eventDate = new Date(this.event.start)
      if (isNaN(eventDate.getTime())) {
        return null
      }
      const toDate = (dateString) => {
        if (!dateString) {
          return null
        }
        const d = new Date(dateString)
        return isNaN(d.getTime()) ? null : d
      }
      const isInRange = (startString, endString) => {
        const start = toDate(startString)
        const end = toDate(endString)
        if (!start || !end) {
          return false
        }
        return eventDate >= start && eventDate <= end
      }
      let found = this.activeSchoolYears.find(schoolYear => isInRange(schoolYear.student_time_start, schoolYear.student_time_end))
      if (!found) {
        found = this.activeSchoolYears.find(schoolYear => isInRange(schoolYear.employee_time_start, schoolYear.employee_time_end))
      }
      return found ? found.id : null
    },
    eventSchoolId () {
      return this.event ? this.event.school_id : null
    },
    klassCalendars () {
      return this.returnCorrectCalendars('klass')
    },
    allUsersInCalIdHash () {
      return this.users.reduce((obj, item) => {
        obj[item.user_calendar_id] = item
        return obj
      }, {})
    },
    userCalendars () {
      const cals = this.calendars.filter(cal => cal.type === 'user' && cal.name !== null && cal.name !== '')
      return this.$helpers.sortArrOnName(cals)
    },
    roomCalendars () {
      const cals = this.calendars.filter(cal => cal.type === 'room' && !cal.archived_at && cal.name !== null && cal.name !== '')
      return this.$helpers.sortArrOnName(cals)
    },
    otherCalendars () {
      return this.returnCorrectCalendars('other')
    },
    kindergartenCalendars () {
      return this.returnCorrectCalendars('kindergarten')
    },
    clubCalendars () {
      return this.returnCorrectCalendars('club')
    },
    studentCalendars () {
      const cals = this.calendars.filter(cal => cal.type === 'student' && !cal.archived_at && cal.name !== null && cal.name !== '')
      return this.$helpers.sortArrOnName(cals)
    },
    calendarId () {
      return this.event ? this.event.calendar_id : null
    },
    items () {
      let cals = []
      if (this.itemType === 'user') {
        if (!this.showTags) {
          cals = this.userCalendars
        }
        let userCalsWithTags = this.userCalendars.map(cal => {
          let tags = this.allUsersInCalIdHash[cal.id] && this.allUsersInCalIdHash[cal.id].user_tags ? this.allUsersInCalIdHash[cal.id].user_tags : []
          this.$set(cal, 'user_tags', tags)
          return cal
        })
        this.tags.forEach(tag => {
          userCalsWithTags.push(tag)
        })
        cals = userCalsWithTags
      }
      if (this.itemType.toLowerCase() === 'room') {
        cals = this.roomCalendars
      }
      if (this.itemType.toLowerCase() === 'klass') {
        cals = this.filterCalendarsByEventSchoolYear(this.klassCalendars)
      }
      if (this.itemType.toLowerCase() === 'student') {
        cals = this.studentCalendars
      }
      if (this.itemType.toLowerCase() === 'other') {
        cals = this.filterCalendarsByEventSchoolYear(this.otherCalendars)
      }
      if (this.itemType.toLowerCase() === 'club') {
        cals = this.clubCalendars
      }
      if (this.itemType.toLowerCase() === 'kindergarten') {
        cals = this.kindergartenCalendars
      }
      return this.calendarId && !this.includeEventCalendar ? cals.filter(calendar => calendar.id !== this.calendarId) : cals
    },
    itemsWithOccupation () {
      return this.items.map(calendar => {
        this.$set(calendar, 'v_occupation', [])
        this.$set(calendar, 'v_presence', [])
        if (this.occupiedItems[calendar.id]) {
          this.$set(calendar, 'v_occupation', this.occupiedItems[calendar.id])
        }
        if (this.presentItems[calendar.id]) {
          this.$set(calendar, 'v_presence', this.presentItems[calendar.id])
        }
        return calendar
      })
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    date () {
      return this.event.start
    }
  },
  watch: {
    items (val) {
      if (val.length > 0) {
        this.setPreSelectedItems()
      }
    },
    eventId (val) {
      if (this.event) {
        this.getDoubleBookedCalendars()
      }
    },
    preSelectedItems () {
      this.setPreSelectedItems()
    }
  },
  methods: {
    updateAndClose () {
      this.activeCalendar = null
      this.$emit('updateAndClose')
    },
    filterCalendarsByEventSchoolYear (cals) {
      const schoolYearId = this.eventSchoolYearId
      if (!schoolYearId) {
        return cals
      }
      let schoolYearName = ''
      const idx = this.activeSchoolYears ? this.activeSchoolYears.findIndex(schoolYear => schoolYear.id === schoolYearId) : -1
      if (idx > -1) {
        schoolYearName = this.activeSchoolYears[idx].name
      }
      return cals.filter(calendar => {
        if (calendar.school_year_id && calendar.school_year_id === schoolYearId) {
          return true
        }
        if (schoolYearName && calendar.year && calendar.year === schoolYearName) {
          return true
        }
        if (!calendar.school_year_id && !calendar.year) {
          return true
        }
        return false
      })
    },
    returnCorrectCalendars (calType) {
      const schoolYearIds = this.activeSchoolYears.map(schoolYear => schoolYear.id)
      const schoolYearYears = this.activeSchoolYears.map(schoolYear => schoolYear.name)
      const calendars = this.calendars.filter(cal => cal.type === calType && cal.name !== null && cal.name !== '')
      const calendarsInActiveYear = calendars.filter(cal => cal.school_year_id ? schoolYearIds.indexOf(cal.school_year_id) > -1 : schoolYearYears.indexOf(cal.year) > -1)
      const calendarInActiveYearMapped = calendarsInActiveYear.map(calendar => {
        return {
          ...calendar,
          name: schoolYearIds.length < 2 ? calendar.name : this.getSchoolYearName(calendar) + ' - ' + calendar.name
        }
      })
      return this.$helpers.sortArrOnName(calendarInActiveYearMapped)
    },
    getSchoolYearName (cal) {
      const idx = this.activeSchoolYears ? this.activeSchoolYears.findIndex(schoolYear => schoolYear.id === cal.school_year_id) : -1
      if (idx > -1) {
        return this.activeSchoolYears[idx]['name']
      }
      return ''
    },
    withPopper (dropdownList, component, {width}) {
      dropdownList.style.width = width
      createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset', options: {
              offset: [0, 0]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({state}) {
              component.$el.classList.toggle('openFromBottom', state.placement === 'top')
            }
          }]
      })
    },
    showCalendar (calendar) {
      const el = document.querySelector('.d-show-event')
      if (el) {
        el.click()
      }
      this.activeCalendar = calendar
    },
    setSelectedItems (val) {
      this.selectedItems = []
      this.selectedItems = val
      this.$emit('update', this.selectedItems)
    },
    isInSelectedItems (calid) {
      return this.selectedItems.findIndex(item => item.id === calid) >= 0
    },
    setPreSelectedItems () {
      if (this.calendars.length < 1) {
        return
      }
      this.selectedItems = []
      this.preSelectedItems.forEach(calendarId => {
        if (calendarId) {
          if (!this.isInSelectedItems(calendarId)) {
            let idx = this.items.findIndex(item => item.id === calendarId)
            if (idx > -1) {
              this.selectedItems.push(this.items[idx])
            }
          }
        }
      })
      if (!this.showTags) {
        this.isLoaded = true
      }
    },
    getTags () {
      docendo.schoolTags(this.event.school_id, data => {
        let tags = []
        let i = 0
        data.forEach(tag => {
          i++
          let oneTag = {
            tag: true,
            id: 999999999999 + i,
            name: tag,
            icon: 'icon-price-tag2'
          }
          tags.push(oneTag)
        })
        this.tags = tags
      })
    },
    createNew (evt) {
      if (this.itemType === 'room') {
        let calendars = [{name: evt, type: this.itemType}]
        docendo.schoolBulkCreateCalendars(this.eventSchoolId, this.activeSchoolYear.id, calendars, success => {
          console.log('success', success)
        }, failure => {
          console.log('failure', failure)
        })
        return
      }
      if (this.itemType === 'user') {
        let users = [{name: evt}]
        docendo.schoolUsersCreate(this.eventSchoolId, users, success => {
          this.$store.dispatch('getSchoolCalendars', window.SCHOOL.id)
          this.newUserCalendarId = success[0].user_calendar_id
        }, failure => {
          console.log('failure', failure)
        })
      }
    },
    getDoubleBookedCalendars () {
      this.isLoading = true
      this.occupiedItems = {}
      this.presentItems = {}
      docendo.schoolAvailabilities(this.eventSchoolId, this.itemType, this.event.start, this.event.end, data => {
        this.isLoading = false
        const dbAnswer = maskHiddenAbsenceTitlesInAvailability(data && data.length > 0 ? JSON.parse(data) : [], this.$store, this.eventSchoolId)
        dbAnswer.forEach(cal => {
          const occEvents = []
          const occPresence = []
          cal.events.forEach(event => {
            if (event.event_id !== this.eventId) {
              if (event.event_type === 'presence') {
                occPresence.push(event)
              } else {
                occEvents.push(event)
              }
            }
          })
          if (occEvents.length > 0) {
            this.$set(this.occupiedItems, cal.calendar_id, occEvents)
          }
          if (occPresence.length > 0) {
            this.$set(this.presentItems, cal.calendar_id, occPresence)
          }
        })
      })
    }
  },
  mounted () {
    this.setPreSelectedItems()
    if (this.showTags) {
      this.getTags()
    }
    if (this.event) {
      this.$nextTick(_ => {
        this.getDoubleBookedCalendars()
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// DSelectCalendars.vue?6fa6da94